import { Command as CommandPrimitive } from "cmdk";

import AdaptiveDialog, {
  AdaptiveDialogContent,
  AdaptiveDialogDescription,
  AdaptiveDialogHeader,
  AdaptiveDialogTitle,
} from "@/lib/components/ui/adaptive-dialog/adaptive-dialog";
import {
  inputVariants,
  type InputVariants,
} from "@/lib/components/ui/input/input";
import { cn } from "@/lib/utils";
import type { ComponentProps } from "react";

export default function Command({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "bg-background-neutral text-foreground flex h-full w-full flex-col overflow-hidden rounded-md",
        className,
      )}
      {...props}
    />
  );
}

export function CommandDialog({
  title = "Command Palette",
  children,
  className,
  description = "Search for a command to run...",
  ...props
}: ComponentProps<typeof AdaptiveDialog> & {
  title?: string;
  className?: string;
  description?: string;
}) {
  return (
    <AdaptiveDialog {...props}>
      <AdaptiveDialogHeader className="sr-only">
        <AdaptiveDialogTitle>{title}</AdaptiveDialogTitle>
        <AdaptiveDialogDescription>{description}</AdaptiveDialogDescription>
      </AdaptiveDialogHeader>
      <AdaptiveDialogContent className={cn("overflow-hidden p-0", className)}>
        <Command className="[&_[cmdk-group-heading]]:text-foreground-pressed **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </AdaptiveDialogContent>
    </AdaptiveDialog>
  );
}

export function CommandInput({
  size = "md",
  className,
  ...props
}: Omit<ComponentProps<typeof CommandPrimitive.Input>, "size"> &
  Pick<InputVariants, "size">) {
  return (
    <CommandPrimitive.Input
      data-slot="command-input"
      className={cn(
        inputVariants({ size, variant: "ghost" }),
        "focus-visible:ring-0 focus-visible:outline-0",
        className,
      )}
      {...props}
    />
  );
}

export function CommandList({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
        className,
      )}
      {...props}
    />
  );
}

export function CommandEmpty({
  ...props
}: ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

export function CommandGroup({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "text-foreground [&_[cmdk-group-heading]]:text-foreground-pressed overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
        className,
      )}
      {...props}
    />
  );
}

export function CommandSeparator({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("bg-border -mx-1 h-px", className)}
      {...props}
    />
  );
}

export function CommandItem({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "data-[selected=true]:bg-foreground/20 data-[selected=true]:text-foreground [&_svg:not([class*='text-'])]:text-foreground-pressed relative flex cursor-default items-center gap-2 rounded-sm px-4 py-3 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 md:px-2 md:py-1.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

export function CommandShortcut({
  className,
  ...props
}: ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "text-foreground-pressed ml-auto text-xs tracking-widest",
        className,
      )}
      {...props}
    />
  );
}
